import type React from "react";
import type {ReactNode} from "react";
import {Box} from "@mui/material";
import {Group, Panel, useDefaultLayout} from "react-resizable-panels";
import {DRAWER_HEADER_HEIGHT_LG, DRAWER_HEADER_HEIGHT_SM} from "~/components/main/drawer/DrawerConstants";
import {RESIZABLE_SPLIT_CLASS, SplitSeparator} from "~/components/resizable-split/SplitSeparator";
import {
    COLLAPSED_PERCENT_MAX,
    type CollapsiblePanel,
    sessionLayoutStorage,
} from "~/components/resizable-split/useCollapsiblePanel";

// Renamed when Atlas moved to the left: widths saved for the old page | Atlas order are not reused.
const SPLIT_ID = "dsh-atlas-left-split";
const PAGE_PANEL = "dsh-page";
const ATLAS_PANEL = "dsh-atlas";
// The Atlas panel opens at its minimum; the gallery keeps 3 cards a row from 1366px wide screens up.
const ATLAS_MIN = "440px";
const ATLAS_MAX = "1100px";
// The page beside the panel never gets narrower than this.
const PAGE_MIN = "480px";

// The shell fills the window under the app bar, so the page panel scrolls on its own and page
// scrolling or the chat's own scrollIntoView never move the Atlas panel.
const SHELL_HEIGHT = {
    xs: `calc(100vh - ${DRAWER_HEADER_HEIGHT_SM}px)`,
    sm: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px)`,
};

type Props = {
    /** The docked Atlas panel, or null while it is closed. */
    atlas: ReactNode | null;
    children: ReactNode;
    /**
     * From useCollapsiblePanel: makes Atlas collapsible, so the page can show its dashboard full
     * screen as on the chat route.
     */
    atlasPanel?: Pick<CollapsiblePanel, "panelRef" | "onResize">;
};

/**
 * Atlas | page split of the Dashboards module, laid out like the chat route: Atlas docked on the
 * left, the gallery or a dashboard on the right, on the same react-resizable-panels Group and
 * SplitSeparator as the chat ↔ dashboard split. The page panel stays mounted while Atlas opens and
 * closes (so an embedded Superset dashboard never reloads) and scrolls on its own; the Atlas panel
 * keeps its pixel width when the window resizes.
 */
export const DashboardsAtlasSplit: React.FC<Props> = ({atlas, children, atlasPanel}) => {
    const {defaultLayout, onLayoutChanged} = useDefaultLayout({
        id: SPLIT_ID,
        storage: sessionLayoutStorage,
        panelIds: atlas ? [ATLAS_PANEL, PAGE_PANEL] : [PAGE_PANEL],
        onlySaveAfterUserInteractions: true,
    });
    // A collapsed Atlas is never remembered: the next Atlas would open invisible.
    const saveLayout: typeof onLayoutChanged = (layout, meta) => {
        if ((layout[ATLAS_PANEL] ?? 100) < COLLAPSED_PERCENT_MAX) return;
        onLayoutChanged(layout, meta);
    };

    return (
        <Box className="dsh-shell" sx={{height: SHELL_HEIGHT}}>
            <Group
                id={SPLIT_ID}
                orientation="horizontal"
                className={`dsh-split ${RESIZABLE_SPLIT_CLASS}`}
                defaultLayout={defaultLayout}
                onLayoutChanged={saveLayout}
            >
                {atlas && (
                    <Panel
                        id={ATLAS_PANEL}
                        defaultSize={ATLAS_MIN}
                        minSize={ATLAS_MIN}
                        maxSize={ATLAS_MAX}
                        groupResizeBehavior="preserve-pixel-size"
                        collapsible={Boolean(atlasPanel)}
                        collapsedSize={0}
                        panelRef={atlasPanel?.panelRef}
                        onResize={atlasPanel?.onResize}
                        className="dsh-split-atlas"
                    >
                        {atlas}
                    </Panel>
                )}
                {atlas && <SplitSeparator id="dsh-atlas-separator"/>}
                <Panel id={PAGE_PANEL} minSize={PAGE_MIN} className="dsh-split-page">
                    {children}
                </Panel>
            </Group>
        </Box>
    );
};
